import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Terminal, ArrowRight } from 'lucide-react';
import { AnimatedDAG } from './AnimatedDAG';

export function HeroSectionV2() {
  const navigate = useNavigate();
  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-surface-900 via-surface-800 to-surface-900" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary-900/20 via-transparent to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 py-20 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Copy */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-6"
            >
              <span className="w-2 h-2 rounded-full bg-primary-400 animate-pulse" />
              Beyond AI Autocomplete
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-text-primary mb-6 leading-tight">
              The Orchestration Layer for{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-accent-400">
                Cognitive Work
              </span>
            </h1>

            <p className="text-lg md:text-xl text-text-secondary mb-8 max-w-xl leading-relaxed">
              Coordinate multi-agent streams, manage persistent context, and keep humans in the loop with a local-first, privacy-preserving runtime.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/dashboard')}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors"
              >
                Start Orchestrating
                <ArrowRight size={18} />
              </motion.button>

              <motion.a
                whileHover={{ scale: 1.02 }}
                href="#demo"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-surface-700 hover:bg-surface-600 text-text-primary font-semibold rounded-lg border border-surface-500 transition-colors"
              >
                Watch Demo
              </motion.a>
            </div>

            {/* CLI Install */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="inline-flex items-center gap-3 px-4 py-2 bg-surface-800 rounded-lg border border-surface-600 font-mono text-sm"
            >
              <Terminal size={16} className="text-text-tertiary" />
              <code className="text-text-secondary">
                npm install <span className="text-primary-400">@cwe/cli</span>
              </code>
              <button className="text-text-tertiary hover:text-text-primary transition-colors">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                </svg>
              </button>
            </motion.div>
          </motion.div>

          {/* Right: Animated DAG */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 to-accent-500/10 rounded-2xl blur-3xl" />
            <div className="relative bg-surface-800/50 backdrop-blur-sm rounded-2xl border border-surface-600 p-6 min-h-[350px]">
              <AnimatedDAG />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
